import React, { useState, useCallback } from "react";

import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  IconButton,
  InputAdornment,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router";

import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";
import { AUTH } from "src/platform/constants/routes";
import debugLogger from "src/platform/utils/debugLogger";

import { OAuthError } from "../types/OAuthError";

import GithubButton from "./GithubButton";
import GoogleButton from "./GoogleButton";

interface SignUpFormData {
  fullName: string;
  email: string;
  password: string;
  organizationName: string;
}

const PASSWORD_MIN_LENGTH = 12;

/**
 * Split a full name into a first/last pair for the legacy signUp payload,
 * which still expects separate `firstName`/`lastName` fields. The first
 * token becomes the first name and the remainder becomes the last name.
 */
const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const trimmed = fullName.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex === -1) {
    return { firstName: trimmed, lastName: "" };
  }
  return {
    firstName: trimmed.slice(0, spaceIndex),
    lastName: trimmed.slice(spaceIndex + 1),
  };
};

const SignUpForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { signUp, loading, error, authInProgress } = useLegacyAuth();
  const [formData, setFormData] = useState<SignUpFormData>({
    fullName: "",
    email: "",
    password: "",
    organizationName: "",
  });
  const [formErrors, setFormErrors] = useState<Partial<SignUpFormData>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  // A17/A18: unified input styling — hidden MUI fieldset with a custom
  // border on the outlined input root and a consistent focus ring.
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? theme.palette.grey[800] : theme.palette.grey[50],
      height: "38px",
      border: `1px solid ${isDark ? theme.palette.grey[600] : theme.palette.grey[200]}`,
      transition: "all 150ms ease",
      "&:hover": {
        borderColor: isDark ? theme.palette.grey[500] : theme.palette.grey[300],
      },
      "&.Mui-focused": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)}`,
      },
      "& fieldset": { border: "none" },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
      "&.Mui-focused": { color: theme.palette.primary.main },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: "14px",
      padding: "10px 14px",
    },
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.text.secondary,
    },
    "& .MuiFormHelperText-root": { color: theme.palette.text.secondary },
  } as const;

  const validateForm = (): boolean => {
    const errors: Partial<SignUpFormData> = {};
    if (!formData.fullName.trim()) errors.fullName = "Full name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      errors.email = "Email is invalid";
    }
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      errors.password = `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
    }
    if (!formData.organizationName.trim()) {
      errors.organizationName = "Organization name is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange =
    (field: keyof SignUpFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (formErrors[field]) {
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!validateForm()) return;

      const { firstName, lastName } = splitFullName(formData.fullName);
      try {
        // NOTE: organizationName is collected here for the signup form
        // (mockup parity). The current signUp API contract accepts only
        // firstName/lastName/email/password; wiring the org name into the
        // backend payload is a separate API packet.
        await signUp({
          firstName,
          lastName,
          email: formData.email,
          password: formData.password,
        });
        setSuccess(true);
        window.setTimeout(() => {
          void navigate(AUTH.LOGIN.path, {
            state: { message: "Account created successfully! Please sign in." },
          });
        }, 1500);
      } catch (err) {
        debugLogger.error("Sign up error:", err);
      }
    },
    [formData, signUp, navigate],
  );

  const handleOAuthSuccess = (user: Record<string, unknown>) => {
    debugLogger.debug("OAuth signup successful:", user);
  };

  const handleOAuthError = (err: OAuthError) => {
    debugLogger.error("OAuth signup error:", err);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 400 }}>
      {/* Heading */}
      <Typography
        component="h1"
        fontWeight={700}
        sx={{ color: "text.primary", mb: 0.5, fontSize: "1.5rem" }}
      >
        Create your account
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Start deploying infrastructure in minutes. No credit card required.
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "8px",
            py: 1.5,
            px: 2,
            fontSize: "13px",
            "& .MuiAlert-icon": { display: "none" },
          }}
        >
          {error}{" "}
          <Link
            component="button"
            type="button"
            onClick={() => navigate(AUTH.LOGIN.path)}
            sx={{
              textDecoration: "none",
              fontWeight: 600,
              color: "primary.dark",
              fontSize: "13px",
            }}
          >
            Sign in
          </Link>{" "}
          instead.
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                mb: 0.75,
                display: "block",
              }}
            >
              Full name
            </Typography>
            <TextField
              id="fullName"
              value={formData.fullName}
              onChange={handleInputChange("fullName")}
              placeholder="Ada Lovelace"
              disabled={loading}
              error={!!formErrors.fullName}
              helperText={formErrors.fullName}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                mb: 0.75,
                display: "block",
              }}
            >
              Email
            </Typography>
            <TextField
              id="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange("email")}
              placeholder="you@company.com"
              disabled={loading}
              error={!!formErrors.email}
              helperText={formErrors.email}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                mb: 0.75,
                display: "block",
              }}
            >
              Password
            </Typography>
            <TextField
              id="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange("password")}
              placeholder="Enter your password"
              autoComplete="new-password"
              disabled={loading}
              error={!!formErrors.password}
              helperText={
                formErrors.password ||
                "At least 12 characters with a mix of letters, numbers, and symbols."
              }
              fullWidth
              required
              inputProps={{ minLength: PASSWORD_MIN_LENGTH }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      tabIndex={-1}
                      size="small"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      title={showPassword ? "Hide password" : "Show password"}
                      sx={{
                        color: "text.secondary",
                        borderRadius: "6px",
                        "&:hover": {
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                mb: 0.75,
                display: "block",
              }}
            >
              Organization name
            </Typography>
            <TextField
              id="organizationName"
              value={formData.organizationName}
              onChange={handleInputChange("organizationName")}
              placeholder="acme-corp"
              disabled={loading}
              error={!!formErrors.organizationName}
              helperText={
                formErrors.organizationName ||
                "Used for URLs and namespaces. You can change this later."
              }
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CorporateFareIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>
        </Box>

        {/* A13: terms as a plain text statement (no checkbox) */}
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            lineHeight: 1.5,
            mt: 2,
            mb: 2,
          }}
        >
          By creating an account, you agree to the{" "}
          <Link
            href="#"
            sx={{
              textDecoration: "none",
              fontWeight: 500,
              color: "primary.dark",
            }}
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="#"
            sx={{
              textDecoration: "none",
              fontWeight: 500,
              color: "primary.main",
            }}
          >
            Privacy Policy
          </Link>
          .
        </Typography>

        <Button
          type="submit"
          fullWidth
          disabled={loading || authInProgress || success}
          sx={{
            padding: "12px 16px",
            minHeight: "41px",
            borderRadius: "10px",
            border: "none",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9375rem",
            ...(success
              ? {
                  color: theme.palette.text.primary,
                  background: theme.palette.success.light,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.success.main, 0.25)}`,
                }
              : {
                  color: theme.palette.primary.contrastText,
                  background: theme.palette.primary.dark,
                  boxShadow: "0 2px 12px rgba(0, 212, 255, 0.25)",
                }),
            transition: "all 150ms ease",
            "&:hover": success
              ? {
                  background: theme.palette.success.light,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.success.main, 0.25)}`,
                }
              : {
                  background: theme.palette.primary.dark,
                  boxShadow: "0 4px 16px rgba(0, 212, 255, 0.35)",
                },
            "&:active": { transform: "scale(0.98)" },
            "&:disabled": success
              ? {
                  bgcolor: theme.palette.success.light,
                  color: theme.palette.text.primary,
                  boxShadow: "0 2px 12px rgba(34, 197, 94, 0.25)",
                }
              : {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                  color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                },
            "&.Mui-focusVisible": {
              boxShadow: `0 0 0 2px #070d1a, 0 0 0 4px ${theme.palette.primary.main}`,
            },
          }}
        >
          {success ? (
            "✓ Account created!"
          ) : loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress
                size={18}
                color="inherit"
                aria-label="Creating account"
              />
              <span>Creating account...</span>
            </Box>
          ) : (
            "Create account"
          )}
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            my: 3,
            color: "text.secondary",
          }}
        >
          <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              whiteSpace: "nowrap",
            }}
          >
            Or sign up with
          </Typography>
          <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <GoogleButton
            label="Continue with Google"
            onSuccess={handleOAuthSuccess}
            onError={handleOAuthError}
            disabled={loading || authInProgress || success}
          />
          <GithubButton
            label="Continue with GitHub"
            onSuccess={handleOAuthSuccess}
            onError={handleOAuthError}
            disabled={loading || authInProgress || success}
          />
        </Box>

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Already have an account?{" "}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate(AUTH.LOGIN.path)}
              sx={{
                textDecoration: "none",
                fontWeight: 600,
                color: "primary.dark",
              }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </form>
    </Box>
  );
};

export default SignUpForm;
